import { wait } from "@utils/general";
import { useCallback, useState } from "react";

type TWorkderFn<T> = (batch: T[], batchIndex: number) => Promise<void>;
interface IBatchProcessOptions {
    limit: number;
    batchSize?: number;
    fluent?: boolean; // adds small wait between worker callbacks to make progress more fluent. The progress jumps by "limit" otherwise.
}

interface UseBatchProcessor<T> {
    run: (items: T[], worker: TWorkderFn<T>, options: IBatchProcessOptions) => Promise<void>,
    progress: number,
    total: number,
    running: boolean
}

// Config for slower requests, e.g. document updates, accounting, where is a risk of timeouts
// when more requests are sent in one batch. Batch size is the key, limit can be adjusted based on BE capacity.
export const SLOW_REQUEST_OPTIONS_CONFIG: IBatchProcessOptions = {
    limit: 5,
    batchSize: 1,
    fluent: true
};

// Balanced config for higher amount of requests, which are not too heavy - save
//  BE performance on batching them in chunks, progress jumps by batchSize.
export const FAST_REQUEST_OPTIONS_CONFIG: IBatchProcessOptions = {
    limit: 15,
    batchSize: 5,
    fluent: true
};

export const PARALELL_LIMIT_BASE = 5;

export async function processBatches<T>(
        items: T[],
        worker: TWorkderFn<T>,
        options: IBatchProcessOptions,
        onProgress?: (done: number, total: number) => void
): Promise<void> {
    const { limit, batchSize = 1 } = options;

    // split chunks according to batchSize
    const batches: T[][] = [];
    for (let i = 0; i < items.length; i += batchSize) {
        batches.push(items.slice(i, i + batchSize));
    }

    let completed = 0;
    const total = batches.length;

    const reportProgress = () => {
        completed++;
        onProgress?.(completed * batchSize, items.length);
    };

    // small delay in ms to make progress more fluent
    let delay = options.fluent ? 500 : 0;
    const executing: Promise<void>[] = [];

    const workerFinished = (promise: Promise<void>, startTime: number) => {
        executing.splice(executing.indexOf(promise), 1);
        if (options.fluent) {
            const elapsed = Date.now() - startTime;
            delay = Math.round(elapsed / limit);
        }
    };

    for (let i = 0; i < total; i++) {
        const startTime = Date.now();
        const p = worker(batches[i], i)
                .then(reportProgress)
                .finally(() => workerFinished(p, startTime));

        executing.push(p);
        if (executing.length >= limit) {
            await Promise.race(executing); // čekáme, až některý doběhne
        }
        if (delay > 0) {
            await wait(delay);
        }
    }

    await Promise.all(executing);
}

export function useBatchProcessor<T>(): UseBatchProcessor<T> {
    const [progress, setProgress] = useState(0);
    const [total, setTotal] = useState(0);
    const [running, setRunning] = useState(false);

    const run = useCallback(
            async (
                    items: T[],
                    worker: (batch: T[], batchIndex: number) => Promise<void>,
                    options: { limit: number; batchSize?: number; fluent?: boolean; }
            ) => {
                setProgress(0);
                setTotal(items.length);
                setRunning(true);

                try {
                    await processBatches(items, worker, options, (done) => setProgress(done));
                } finally {
                    setRunning(false);
                }
            },
            []
    );

    return { run, progress, total, running };
}